import { Tooltip, useMediaQuery } from '@mui/material';
import {
  CompositeFilterDescriptor,
  DataResult,
  DataSourceRequestState,
  FilterDescriptor
} from '@progress/kendo-data-query';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridColumn as Column,
  GridExpandChangeEvent,
  GridSortChangeEvent,
  GridToolbar
} from '@progress/kendo-react-grid';
import { sortBy } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { breakpoints } from 'src/assets/_responsive-mixin';
import {
  ActionButton,
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import {
  Checkbox,
  getCheckboxFilterState
} from 'src/components/shared/Checkbox/Checkbox';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { useColumns } from 'src/components/shared/DataGrid/ColumnMenu';
import { ComboBoxFilterCell } from 'src/components/shared/DataGrid/ComboBoxFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { DateTimeCell } from 'src/components/shared/DataGrid/DateCell';
import { DateRangeFilterCell } from 'src/components/shared/DataGrid/DateRangeFilterCell';
import {
  extractValuesFromMultiSelectFilter,
  getMultiSelectFilterState,
  MultiSelectFilterCell
} from 'src/components/shared/DataGrid/MultiSelectFilterCell';
import { StatusCircle } from 'src/components/shared/StatusCircle/StatusCircle';
import { GradeDto } from 'src/store/grades/grade';
import {
  ProductDetailDto,
  ProductDto,
  ProductStatus,
  ProductStatusDto
} from 'src/store/products/product';
import { getCorrectedValue } from 'src/store/products/product.utils';
import { ProductParameterDto } from 'src/store/products/productParameter';
import { ProductApi } from 'src/store/products/products.api';

import { ProductTypeDto } from 'src/store/producttypes/producttype';
import { RunSystemDto } from 'src/store/runsystems/runsystem';
import { SegmentDto } from 'src/store/segments/segment';
import { UserRole } from 'src/store/users/user';
import { Authorization } from '../Auth/Authorization';
import { productStatusColorMap } from './Product/ProductDetailsForm';
import { productListColumnWidths } from './ProductListColumnWidths';
import {
  getProductColumns,
  ProductsListColumnMenu
} from './ProductsListColumnMenu';
import { ProductsListMobile } from './ProductsListMobile';

type ProductListProps = {
  data: DataResult;
  grades: GradeDto[];
  runSystems: RunSystemDto[];
  productTypes: ProductTypeDto[];
  segments: SegmentDto[];
  productStatuses: ProductStatusDto[];
  loading: boolean;
  requestState: DataSourceRequestState;
  onEdit: (product: ProductDto) => void;
  onEditRun: (product: ProductDto) => void;
  onDelete: (product: ProductDto) => void;
  onCopy: (product: ProductDto) => void;
  onSortChange: (event: GridSortChangeEvent) => void;
  onFilterChange: (filter: CompositeFilterDescriptor | undefined) => void;
  onResetFilters: () => void;
  onPageChange: (skip: number, take: number) => void;
  onPrintLabels: (product: ProductDto) => void;
  onDownloadDeliveryNote: (product: ProductDto) => void;
  onInitExporter: (exporter: ExcelExport) => void;
};

export const ProductsList = React.memo((props: ProductListProps) => {
  const {
    data,
    grades,
    runSystems,
    productTypes,
    segments,
    productStatuses,
    loading,
    requestState,
    onEdit,
    onEditRun,
    onDelete,
    onCopy,
    onSortChange,
    onFilterChange,
    onResetFilters,
    onPageChange,
    onPrintLabels,
    onDownloadDeliveryNote,
    onInitExporter
  } = props;

  const { t } = useTranslation();

  const largeScreen = useMediaQuery(`(min-width: ${breakpoints.large})`);

  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [selectedRunSystems, setSelectedRunSystems] = useState<string[]>([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>(
    []
  );
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);

  const [currentProduct, setCurrentProduct] =
    useState<Nullable<ProductDetailDto>>(null);

  const [products, setProducts] = useState<DataResult>({
    data: [],
    total: 0
  } as DataResult);

  const [hasLotNo, setHasLotNo] = useState<boolean>(false);
  const [hasNorlabNo, setHasNorlabNo] = useState<boolean>(false);

  const { columns, isColumnVisible, onColumnsChange } = useColumns<ProductDto>(
    getProductColumns()
  );

  useEffect(() => {
    setSelectedGrades(
      extractValuesFromMultiSelectFilter(requestState.filter, 'gradeName')
    );
    setSelectedRunSystems(
      extractValuesFromMultiSelectFilter(requestState.filter, 'runSystemName')
    );
    setSelectedProductTypes(
      extractValuesFromMultiSelectFilter(requestState.filter, 'productTypeName')
    );
    setSelectedSegments(
      extractValuesFromMultiSelectFilter(requestState.filter, 'segmentName')
    );
    setHasLotNo(
      (requestState.filter?.filters ?? []).find(
        f =>
          (f as FilterDescriptor).field === 'LotNo' &&
          (f as FilterDescriptor).operator === 'isnotnull'
      ) !== undefined
    );
    setHasNorlabNo(
      (requestState.filter?.filters ?? []).find(
        f =>
          (f as FilterDescriptor).field === 'NorlabNo' &&
          (f as FilterDescriptor).operator === 'isnotnull'
      ) !== undefined
    );
  }, [requestState.filter]);

  useEffect(() => {
    setProducts(data);
  }, [data]);

  useEffect(() => {
    setProducts({
      ...products,
      data: products.data.map(product =>
        product.id === currentProduct?.id
          ? {
              ...product,
              expanded: true,
              productParameters: sortBy(
                currentProduct?.productParameters ?? [],
                productParameter =>
                  productParameter.parameter?.name?.toLowerCase(),
                'parameterName'
              )
            }
          : product
      )
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProduct]);

  type FilterField =
    | 'gradeName'
    | 'runSystemName'
    | 'productTypeName'
    | 'segmentName';

  const filterSetters = {
    gradeName: setSelectedGrades,
    runSystemName: setSelectedRunSystems,
    productTypeName: setSelectedProductTypes,
    segmentName: setSelectedSegments
  };

  const onMultiSelect = (field: FilterField, value: string[]) => {
    filterSetters[field](value);
    onFilterChange(
      getMultiSelectFilterState(requestState.filter, field, value)
    );
  };

  const onProductDetailClick = async (event: GridExpandChangeEvent) => {
    if (event.dataItem.expanded) {
      setProducts({
        ...products,
        data: products.data.map(product =>
          product.id === event.dataItem.id
            ? {
                ...product,
                expanded: false
              }
            : product
        )
      });
    } else {
      const response = await ProductApi.getById(event.dataItem.id);
      if (response.ok) {
        setCurrentProduct(response.data);
      }
    }
  };

  const onHasLotNo = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setHasLotNo(checked);
    onFilterChange(
      getCheckboxFilterState(
        requestState.filter,
        !checked,
        'LotNo',
        'isnotnull',
        null
      )
    );
  };

  const onHasNorlabNo = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setHasNorlabNo(checked);
    onFilterChange(
      getCheckboxFilterState(
        requestState.filter,
        !checked,
        'NorlabNo',
        'isnotnull',
        null
      )
    );
  };

  return (
    <Fragment>
      {largeScreen && (
        <ExcelExport
          data={data.data}
          ref={onInitExporter}
          columns={[
            ...(isColumnVisible(columns, 'runNo')
              ? [{ field: 'runNo', title: 'Run nr' }]
              : []),
            ...(isColumnVisible(columns, 'runSystemName')
              ? [{ field: 'runSystemName', title: 'Runsystem' }]
              : []),
            ...(isColumnVisible(columns, 'runDate')
              ? [{ field: 'runDate', title: 'RunDato' }]
              : []),
            ...(isColumnVisible(columns, 'runText')
              ? [{ field: 'runText', title: 'RunTekst' }]
              : []),
            ...(isColumnVisible(columns, 'lotText')
              ? [{ field: 'lotText', title: 'LotTekst' }]
              : []),
            ...(isColumnVisible(columns, 'gradeName')
              ? [{ field: 'gradeName', title: 'Grad' }]
              : []),
            ...(isColumnVisible(columns, 'lotNo')
              ? [{ field: 'lotNo', title: 'Lot nr' }]
              : []),
            ...(isColumnVisible(columns, 'lotDate')
              ? [{ field: 'lotDate', title: 'LotDato' }]
              : []),
            ...(isColumnVisible(columns, 'norlabNo')
              ? [{ field: 'norlabNo', title: 'Norlab nr' }]
              : []),
            ...(isColumnVisible(columns, 'productStatusName')
              ? [{ field: 'productStatusName', title: 'Produktstatus' }]
              : []),
            ...(isColumnVisible(columns, 'productTypeName')
              ? [{ field: 'productTypeName', title: 'Produkttype' }]
              : []),
            ...(isColumnVisible(columns, 'segmentName')
              ? [{ field: 'segmentName', title: 'Segment' }]
              : []),
            ...(isColumnVisible(columns, 'specificationNo')
              ? [{ field: 'specificationNo', title: 'PSP nr' }]
              : []),
            ...(isColumnVisible(columns, 'specificationName')
              ? [{ field: 'specificationName', title: 'PSP' }]
              : []),
            ...(isColumnVisible(columns, 'mixtureNo')
              ? [{ field: 'mixtureNo', title: 'Fra blanding' }]
              : []),
            ...(isColumnVisible(columns, 'mixtures')
              ? [{ field: 'mixtures', title: 'I blandinger' }]
              : []),
            ...(isColumnVisible(columns, 'description')
              ? [{ field: 'description', title: 'Beskrivelse' }]
              : [])
          ]}
        >
          <DataGrid
            data={products}
            loading={loading}
            pageable
            resizable
            filter={requestState.filter}
            sort={requestState.sort}
            skip={requestState.skip}
            take={requestState.take}
            pageSize={20}
            onSortChange={onSortChange}
            onFilterChange={event => {
              onPageChange(0, 20);
              onFilterChange(event.filter);
            }}
            onPageChange={onPageChange}
            onRowClick={({ dataItem }) => onEdit(dataItem)}
            expandField="expanded"
            detail={props => (
              <ProductParameters
                parameters={props.dataItem.productParameters}
              />
            )}
            onExpandChange={onProductDetailClick}
          >
            <GridToolbar>
              <ProductsListColumnMenu
                selectedColumns={columns}
                onSelectedColumnsChange={onColumnsChange}
              />
              <Checkbox
                className="ml-32"
                label={t('product.hasLotNo')}
                checked={hasLotNo}
                name="hasLotNo"
                onChange={onHasLotNo}
              />
              <Checkbox
                label={t('product.hasNorlabNo')}
                checked={hasNorlabNo}
                name="hasNorlabNo"
                onChange={onHasNorlabNo}
              />
              <SecondaryButton
                label="Nullstill filter"
                name="resetFilters"
                className="ml-8"
                onClick={onResetFilters}
                disabled={
                  !requestState.filter ||
                  requestState.filter.filters.length === 0
                }
              />
            </GridToolbar>
            {isColumnVisible(columns, 'runNo') && (
              <Column
                field="runNo"
                title={t('product.runNo')}
                filter="numeric"
                width={productListColumnWidths['runNo']}
              />
            )}
            {isColumnVisible(columns, 'runSystemName') && (
              <Column
                field="runSystemName"
                title={t('runSystem.singular')}
                width={productListColumnWidths['runSystemName']}
                filterCell={props => (
                  <MultiSelectFilterCell
                    {...props}
                    value={selectedRunSystems}
                    options={runSystems}
                    optionField="name"
                    onValueChange={value =>
                      onMultiSelect('runSystemName', value)
                    }
                  />
                )}
              />
            )}
            {isColumnVisible(columns, 'runDate') && (
              <Column
                field="runDate"
                title={t('product.runDate')}
                cell={props => <DateTimeCell {...props} />}
                filterCell={props => (
                  <DateRangeFilterCell
                    {...props}
                    requestState={requestState}
                    onFilterChange={onFilterChange}
                  />
                )}
                width={productListColumnWidths['runDate']}
              />
            )}
            {isColumnVisible(columns, 'runText') && (
              <Column
                field="runText"
                title={t('product.runText')}
                width={productListColumnWidths['runText']}
              />
            )}
            {isColumnVisible(columns, 'lotText') && (
              <Column
                field="lotText"
                title={t('product.lotText')}
                width={productListColumnWidths['lotText']}
              />
            )}
            {isColumnVisible(columns, 'gradeName') && (
              <Column
                field="gradeName"
                title={t('grade.singular')}
                width={productListColumnWidths['gradeName']}
                filterCell={props => (
                  <MultiSelectFilterCell
                    {...props}
                    value={selectedGrades}
                    options={grades}
                    optionField="name"
                    onValueChange={value => onMultiSelect('gradeName', value)}
                  />
                )}
              />
            )}
            {isColumnVisible(columns, 'lotNo') && (
              <Column
                field="lotNo"
                title={t('product.lotNo')}
                width={productListColumnWidths['lotNo']}
                filter="numeric"
              />
            )}
            {isColumnVisible(columns, 'lotDate') && (
              <Column
                field="lotDate"
                title={t('product.lotDate')}
                cell={props => <DateTimeCell {...props} />}
                filterable={false}
                width={productListColumnWidths['lotDate']}
              />
            )}
            {isColumnVisible(columns, 'norlabNo') && (
              <Column
                field="norlabNo"
                title={t('product.norlabNo')}
                width={productListColumnWidths['norlabNo']}
                filter="numeric"
              />
            )}
            {isColumnVisible(columns, 'productStatusName') && (
              <Column
                field="productStatusName"
                title={t('productStatus.singular')}
                width={productListColumnWidths['productStatusName']}
                filterCell={props => (
                  <ComboBoxFilterCell
                    {...props}
                    options={productStatuses}
                    optionField="name"
                  />
                )}
                cell={({ dataItem }) => (
                  <td>
                    <StatusCircle
                      color={
                        productStatusColorMap[
                          dataItem.productStatusId as ProductStatus
                        ]
                      }
                      className="mr-16"
                    />
                    {dataItem.productStatusName}
                  </td>
                )}
              />
            )}
            {isColumnVisible(columns, 'productTypeName') && (
              <Column
                field="productTypeName"
                title={t('productType.singular')}
                width={productListColumnWidths['productTypeName']}
                filterCell={props => (
                  <MultiSelectFilterCell
                    {...props}
                    value={selectedProductTypes}
                    options={productTypes}
                    optionField="name"
                    onValueChange={value =>
                      onMultiSelect('productTypeName', value)
                    }
                  />
                )}
              />
            )}
            {isColumnVisible(columns, 'segmentName') && (
              <Column
                field="segmentName"
                title={t('segment.singular')}
                width={productListColumnWidths['segmentName']}
                filterCell={props => (
                  <MultiSelectFilterCell
                    {...props}
                    value={selectedSegments}
                    options={segments}
                    optionField="name"
                    onValueChange={value => onMultiSelect('segmentName', value)}
                  />
                )}
              />
            )}
            {isColumnVisible(columns, 'specificationNo') && (
              <Column
                field="specificationNo"
                title={t('specification.pspNo')}
                width={productListColumnWidths['specificationNo']}
              />
            )}
            {isColumnVisible(columns, 'specificationName') && (
              <Column
                field="specificationName"
                title={t('specification.psp')}
                width={productListColumnWidths['specificationName']}
              />
            )}
            {isColumnVisible(columns, 'mixtureNo') && (
              <Column
                field="mixtureNo"
                title={t('product.mixture')}
                width={productListColumnWidths['mixtureNo']}
                filterable={false}
              />
            )}
            {isColumnVisible(columns, 'mixtures') && (
              <Column
                field="mixtures"
                title={t('product.mixtures')}
                width={productListColumnWidths['mixtures']}
                filterable={false}
              />
            )}
            {isColumnVisible(columns, 'description') && (
              <Column
                field="description"
                title={t('props.description')}
                width={productListColumnWidths['description']}
              />
            )}
            <Column
              width="280px"
              filterable={false}
              cell={props => {
                const isEditDisabled =
                  props.dataItem.productStatusId !== ProductStatus.InProduction;
                const editDisabledReason = isEditDisabled
                  ? t('disabledReason.notInProduction')
                  : '';

                const isDeliveryNoteDisabled =
                  props.dataItem.lotNo === null || props.dataItem.lockWaybill;
                const deliveryNoteDisabledReason =
                  props.dataItem.lotNo === null
                    ? t('disabledReason.noLot')
                    : props.dataItem.lockWaybill
                    ? t('disabledReason.noWaybill')
                    : '';
                return (
                  <td>
                    <Tooltip
                      title={isEditDisabled ? editDisabledReason : ''}
                      disableHoverListener={!isEditDisabled}
                    >
                      <span>
                        <ActionButton
                          icon="la la-edit"
                          onClick={() => onEditRun(props.dataItem)}
                          className="mr-8"
                          title={t('label.singular')}
                          disabled={isEditDisabled}
                        />
                      </span>
                    </Tooltip>
                    <ActionButton
                      icon="la la-print"
                      onClick={() => onPrintLabels(props.dataItem)}
                      className="mr-8"
                      title={t('label.singular')}
                    />
                    <Tooltip
                      title={
                        isDeliveryNoteDisabled ? deliveryNoteDisabledReason : ''
                      }
                      disableHoverListener={!isDeliveryNoteDisabled}
                    >
                      <span>
                        <ActionButton
                          icon="la la-truck"
                          className="mr-8"
                          onClick={() => onDownloadDeliveryNote(props.dataItem)}
                          title={t('waybill.singular')}
                          disabled={isDeliveryNoteDisabled}
                        />
                      </span>
                    </Tooltip>
                    <Authorization role={UserRole.Admin}>
                      <ActionButton
                        icon="la la-copy"
                        onClick={() => onCopy(props.dataItem)}
                        className="mr-8"
                        title={t('product.copy')}
                      />
                    </Authorization>
                    <DeleteButton onClick={() => onDelete(props.dataItem)} />
                  </td>
                );
              }}
            />
          </DataGrid>
        </ExcelExport>
      )}

      {!largeScreen && <ProductsListMobile {...props} />}
    </Fragment>
  );
});

const ProductParameters: React.FC<{ parameters: ProductParameterDto[] }> = ({
  parameters
}) => {
  const { t } = useTranslation();

  return (
    <DataGrid items={parameters} style={{ marginTop: 20 }}>
      <Column field="parameter.name" title={t('parameter.singular')} />
      <Column field="method.name" title={t('method.singular')} />
      <Column field="instrument.name" title={t('instrument.singular')} />
      <Column
        field="measuredValue"
        title={t('parameter.measuredValue')}
        format="{0:0.000}"
      />
      <Column
        field="correctionPart"
        title={t('parameter.correctionPartUsed')}
        format="{0:0.000}"
      />
      <Column
        title={t('parameter.correctedValue')}
        cell={({ dataItem }) => {
          const value = getCorrectedValue(dataItem)?.toFixed(3);
          return <td>{value !== null ? value : ''}</td>;
        }}
      />
      <Column field="user.initials" title={t('user.initials')} />
      <Column
        field="toNorlab"
        title={t('parameter.toNorlab')}
        filterable={false}
        cell={props => <BooleanCell {...props} />}
      />
    </DataGrid>
  );
};
